import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetPaymentMethods } from "src/api/generic";
import {
  useAddOCEventPaymentMutation,
  useAddOCTransactionPaymentMutation,
  useAddOCVendorBalancePaymentMutation,
} from "src/api/operation-cost/transactions";
import { LoadingButton } from "@mui/lab";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import { useGetAccountEvents } from "src/api/purchasing/purchasing";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers";
import {
  useAddOCOwnerPaymentMutation,
  useGetCapitalList,
} from "src/api/settings/capital";
import { authState } from "src/store/auth";
import { useGetSVendorBalance } from "src/api/operation-cost/vendors";
import InfoBox from "src/shared/components/info-box";

const schema = yup.object().shape({
  type: yup
    .string()
    .required("Required")
    .oneOf(["payment", "event", "owner", "vendorBalance"] as const),
  amount: yup
    .string()
    .required("Required")
    .test(
      "min-value",
      "Must be greater than 0",
      (value) => parseFloat(value) > 0,
    ),
  payment_method_id: yup.string().when("type", {
    is: (value: string) => value === "payment",
    then: (value) => value.required("Required"),
  }),
  date: yup.date().when("type", {
    is: (value: string) => value !== "event",
    then: (value) => value.required("Required"),
  }),
  eventId: yup.string().when("type", {
    is: (value: string) => value === "event",
    then: (value) => value.required("Required"),
  }),
  ownerId: yup.string().when("type", {
    is: (value: string) => value === "owner",
    then: (value) => value.required("Required"),
  }),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  handleClose: () => void;
  refetch: Function;
  id: number;
  row: any;
}

const AddTransactionPaymentPopup = (props: IProps) => {
  const { open, handleClose, refetch, id, row } = props;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const auth = useRecoilValue(authState);
  const company = auth?.user?.company;
  const [files, setFiles] = useState<any>([]);
  const { t } = useTranslation("operationCost");
  const { t: purchasingT } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");

  //React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      type: "payment",
    },
  });

  //APIS
  const { data: paymentsData } = useGetPaymentMethods();
  const { mutate, status, isPending, error } =
    useAddOCTransactionPaymentMutation();
  const {
    mutate: mutateEvent,
    status: statusEvent,
    isPending: isPendingEvent,
    error: errorEvent,
  } = useAddOCEventPaymentMutation();
  const { data: ownersData } = useGetCapitalList();
  const {
    mutate: mutateOwner,
    status: statusOwner,
    isPending: isPendingOwner,
    error: errorOwner,
  } = useAddOCOwnerPaymentMutation();
  const {
    mutate: mutateBalance,
    status: statusBalance,
    isPending: isPendingBalance,
    error: errorBalance,
  } = useAddOCVendorBalancePaymentMutation();
  const { data: accountEventsData } = useGetAccountEvents(row.vendor_id);
  const { data: vendorBalanceData } = useGetSVendorBalance(row.vendor_id);
  const vendorBalance = vendorBalanceData?.data?.balance ?? 0;

  const accountEventsList = accountEventsData?.data ?? [];

  const selectedOwner = (ownersData?.data ?? []).find(
    (owner: any) => owner.id === Number(watch("ownerId")),
  );

  const selectedEvent = accountEventsList.find(
    (item: any) => item.id === Number(watch("eventId")),
  );

  const accountEventCreditRemaining =
    selectedEvent?.amount - selectedEvent?.used || 0;

  const onClose = () => {
    handleClose();
    reset();
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(data.date)
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    if (
      data.type === "event" &&
      data.amount &&
      +data.amount > accountEventCreditRemaining
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${t("eventBalance")}: ${accountEventCreditRemaining}`,
        },
      ]);
      return;
    }

    // to check vendor balance
    if (
      data.type === "vendorBalance" &&
      data.amount &&
      +data.amount > vendorBalance
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balance")}: ${vendorBalance}`,
        },
      ]);
      return;
    }

    const dataToSend: any = {
      invoice_id: id,
      amount: +data.amount,
      ...(data.type !== "event" && {
        date: moment(data.date).format("YYYY-MM-DD"),
      }),
      ...(data.type === "payment" && {
        payment_method_id: data.payment_method_id,
      }),
      ...(data.type === "event" && { purchase_event_id: data.eventId }),
      ...(data.type === "owner" && { capital_id: data.ownerId }),
    };
    const formData = new FormData();
    // append data
    for (const key in dataToSend) {
      formData.append(key, dataToSend[key]);
    }
    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }
    if (data.type === "event") {
      mutateEvent(formData);
    } else if (data.type === "owner") {
      mutateOwner(formData);
    } else if (data.type === "vendorBalance") {
      mutateBalance(formData);
    } else {
      mutate(formData);
    }
  };

  //Add OC transaction payment success and error handling
  useEffect(() => {
    if (status === "success" || statusEvent === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error" || statusEvent === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.data.errors?.[0].value ??
            errorEvent?.data.errors?.[0].value ??
            generalT("somethingWrong"),
        },
      ]);
    }
  }, [status, statusEvent]);

  //Add OC Owner payment success and error handling
  useEffect(() => {
    if (statusOwner === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (statusOwner === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorOwner?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusOwner]);

  //Add vendor payment success and error handling
  useEffect(() => {
    if (statusBalance === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (statusBalance === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorBalance?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusBalance]);

  console.log({ errors, row });

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", sm: 547 },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {purchasingT("payment")}
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Stack direction={"row"} alignItems={"center"} gap={3}>
              <Typography>{purchasingT("paymentType")}</Typography>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  width: "fit-content",
                  ml: { xs: "auto", sm: "unset" },
                }}
              >
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      key={field.value}
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      {["payment", "event", "owner", "vendorBalance"].map(
                        (item) => (
                          <FormControlLabel
                            value={item}
                            control={
                              <Radio
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                                size="small"
                              />
                            }
                            label={
                              item === "vendorBalance"
                                ? `${row.vendor_name} (${vendorBalance})`
                                : item === "event"
                                  ? generalT("advancePayment")
                                  : purchasingT(item)
                            }
                            sx={{ color: "#98A2B3" }}
                          />
                        ),
                      )}
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Stack>

            {watch("type") === "vendorBalance" && (
              <InfoBox
                text={purchasingT("formBalanceAdvancePaymentExcluded", {
                  source: row.vendor_name,
                })}
              />
            )}

            {watch("type") === "payment" ? (
              <Stack spacing={2}>
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.payment_method_id}
                >
                  <InputLabel id="demo-simple-select-label">
                    {t("paymentMethod")}
                  </InputLabel>
                  <Controller
                    name="payment_method_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label={t("paymentMethod")}
                      >
                        {paymentsData?.data
                          ?.filter((pay) => pay.status)
                          .map(({ id, name }) => {
                            return (
                              <MenuItem key={id} value={`${id}`}>
                                {name}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.payment_method_id?.message}
                  </FormHelperText>
                </FormControl>
              </Stack>
            ) : null}

            {watch("type") === "event" ? (
              <FormControl fullWidth size="small" error={!!errors.eventId}>
                <InputLabel id="demo-simple-select-label">
                  {purchasingT("event")}
                </InputLabel>
                <Controller
                  name="eventId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={purchasingT("event")}
                    >
                      {accountEventsList.map(({ id, reference }: any) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {reference}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.eventId?.message}
                </FormHelperText>
              </FormControl>
            ) : null}

            {watch("type") === "owner" ? (
              // owner
              <FormControl fullWidth size="small" error={!!errors.ownerId}>
                <InputLabel id="demo-simple-select-label">
                  {purchasingT("owner")}
                </InputLabel>
                <Controller
                  name="ownerId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={purchasingT("event")}
                    >
                      {ownersData?.data.map(({ id, name }: any) => {
                        return (
                          <MenuItem key={id} value={`${id}`}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {errors.eventId?.message}
                </FormHelperText>
              </FormControl>
            ) : null}

            {/* Amount */}
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    fullWidth
                    label={t("amount")}
                    size="small"
                    {...field}
                    error={!!errors.amount}
                    helperText={errors.amount?.message}
                  />
                )}
              />
              {watch("type") === "event" && (
                <Typography whiteSpace={"nowrap"}>
                  {t("eventBalance")}: {accountEventCreditRemaining}
                </Typography>
              )}
            </Stack>

            {/* date */}
            {watch("type") !== "event" && (
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.date,
                        helperText: t(`${errors.date?.message ?? ""}`),
                        size: "small",
                      },
                    }}
                    format={"dd/MM/yyyy"}
                    label={t("paymentDate")}
                  />
                )}
              />
            )}
          </Stack>
          {/* files */}
          <Box width={"100%"} mt={2}>
            <FilePondUploader
              onUpload={(e) => setFiles(e)}
              maxFiles={3}
              maxFileSize={3}
            />
          </Box>
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={onClose}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              loading={isPending || isPendingEvent || isPendingOwner}
            >
              {generalT("confirm")}
            </LoadingButton>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
};

export default AddTransactionPaymentPopup;
